'use strict';

import {
  Expr, Named, FormatOptions, TermInfo, toposort,
  App, Alias, FreeVar, Lambda, control, InferOptions, Atomic,
} from './expr';
import { builtin } from './parser';
import { Case, Quest } from './quest';

/**
 *   Extra utilities that do not belong in the core.
 */

// --- Types ---

export type SearchCallbackResult<T> = {
  /** 0 = keep the term to compute further terms (the default);
   *  n<0 = discard the term; n>0 = keep the term, but delay its use by n generations
   */
  offset?: number;
  /** if not undefined, this means the term is of interest and will appear in the result */
  found?: T;
  /** true = stop the search immediately */
  stop?: boolean;
};
export type SearchOptions = {
  /** maximum generation to search for */
  depth?: number;
  /** maximum number of tried terms before giving up */
  tries?: number;
  /** whether to call infer() on each term; default true */
  infer?: boolean;
  /** maximum number of steps in infer(), see {@link infer} */
  max?: number;
  /** maximum number of arguments in infer(), see {@link infer} */
  maxArgs?: number;
  /** maximum size of a term in infer() before the calculation is aborted; see {@link infer} */
  maxSize?: number;
  /** prevents skipping equivalent terms. Always true if infer is false. */
  noskip?: boolean;
  /** minimum number of tries between progress yields, default 1000 */
  progressInterval?: number;
};
export type SearchCallback<T> = (e: Expr, props: TermInfo) => (SearchCallbackResult<T> | number | undefined);
/** Yielded by the search generator on every progress tick and on each found term. */
export type SearchProgress<T> = {
  /** The found expression; present only when found is defined. */
  expr?: Expr;
  /** Why we should care about this term (e.g. when construction multiple terms from a basis)
   *  or simply true if there are no important details aside from the term itself.
   */
  found?: T;
  /** only true on the last yield */
  stop?: boolean;
  /** true = a new generation has started */
  newGen?: boolean;
  /** The current generation number, starting from 0 for the seed generation. */
  gen: number;
  /** The number of terms generated so far. */
  total: number;
  /** The number of terms excluding duplicates. Will == total if noskip is true or infer is false. */
  probed: number;
  /** The entire generation cache reference.
   * This is actually a mutable value and should not be modified by the consumer,
   * and deep-copied in case one wants a true snapshot.
   */
  cache: Expr[][];
};

export type EquivResult = {
  steps: number,
  equal: boolean,
  normal: boolean,
  canonical: [Expr | null, Expr | null],
};

// poor man's zod
const formatSchema: Record<string, (arg0: unknown) => string | undefined> = {
  html:      x => typeof x === 'boolean' ? undefined : 'must be a boolean',
  terse:     x => typeof x === 'boolean' ? undefined : 'must be a boolean',
  space:     x => typeof x === 'string' ? undefined : 'must be a string',
  brackets:  isStringPair,
  var:       isStringPair,
  around:    isStringPair,
  redex:     isStringPair,
  lambda:    isStringTriple,
  inventory: x => {
    if (typeof x !== 'object' || x === null || x.constructor !== Object)
      return 'must be an object, not ' + (x?.constructor?.name ?? typeof x);
    const refined = x as Record<string, unknown>;
    for (const key of Object.keys(refined)) {
      if (!(refined[key] instanceof Expr))
        return 'key ' + key + 'is not an Expr';
    }
    return undefined;
  }
}

// --- Exported functions (alphabetical) ---

/**
 *   Converts an unknown object into a FormatOptions, or returns an error if it is not valid.
 *   A null/undefined counts as an empty options object (and is thus valid).
 */
function checkFormatOptions (raw: unknown): { value: FormatOptions } | { error: Record<string, string> } {
  if (raw === null || raw === undefined)
    return { value: {} };

  if (typeof raw !== 'object' || Array.isArray(raw) || raw.constructor !== Object)
    return { error: { object: 'Format options must be an object, not ' + (raw?.constructor?.name ?? typeof raw) } };

  const rec = raw as Record<string, unknown>;
  const error: Record<string, string> = {};

  for (const key in rec) {
    if (formatSchema[key]) {
      const err = formatSchema[key](rec[key]);
      if (err)
        error[key] = err;
    } else
      error[key] = 'unknown option';
  }

  return Object.keys(error).length > 0 ? { error } : { value: rec as FormatOptions };
}

/**
 * Find out if two expressions are computationally equivalent.
 *
 * Unlike equals(), this function will attempt to normalize both expressions
 * before comparing.
 *
 * @experimental
 * @param e1
 * @param e2
 * @param options
 */
function equiv (e1: Expr, e2: Expr, options = {}): EquivResult {
  let steps = 0;
  const [n1, n2] = [e1, e2].map( x => x.traverse(e => {
    const props = e.infer(options);
    steps += props.steps ?? 0;
    return props.expr;
  }));
  const normal = !!(n1 && n2);
  return {
    steps,
    normal,
    equal:     normal ? n1.equals(n2) : false,
    canonical: [n1, n2],
  }
}

/**
 *  Recursively replace all instances of Expr in a data structure with
 *       respective string representation using the format() options.
 *       Objects of other types and primitive values are eft as is.
 *
 *       May be useful for debugging or diagnostic output.
 *
 * @experimental
 */
// yes allow any in this function, it's pattern matched into correct classes during traverse
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function deepFormat (obj: any, options : FormatOptions = {}): any {
  if (obj instanceof Expr)
    return obj.format(options);
  // TODO for quests, use toJSON when it's ready
  if (obj instanceof Quest)
    return 'Quest(' + obj.name + ')';
  if (obj instanceof Case)
    return 'Quest.Case';
  if (Array.isArray(obj))
    return obj.map(item => deepFormat(item, options));
  if (typeof obj !== 'object' || obj === null || obj.constructor !== Object)
    return obj;

  // default = plain object
  const out: { [key: string]: unknown } = {};
  for (const key in obj)
    out[key] = deepFormat(obj[key], options);

  return out;
}

/**
 * @experimental
 * @template T - The 'evidence' type showing why a term is of interest.
 * Can be `true` if we only care about the term(s) themseves, of just want one term.
 *
 * Given a seed set of expressions, search for expression(s) that match the predicate
 * by applying already known expressions to one another.
 * Seen terms are organized into generations, with gen(seed) = 0
 * and gen(g f) == gen(g) + gen(f) + 1 + an optional offset
 * (e.g. we want to study duplicating terms later than the discarding ones).
 *
 * The predicate receives a term and its inferred properties
 * (if `infer` is false, a dummy constant will be given instead)
 * and returns an object with the following optional properties:
 * - `found`: T - if present, this term is of interest and will be yielded in the result;
 * - `stop`: boolean - if true, the search stops after yielding, independent of `found`;
 * - `offset`: a number with the following meaning:
 *   - negative: discard the expression entirely;
 *   - 0: keep the expression to compute further terms;
 *   - n >0: keep the expression, but delay its use by n generations.
 * Or it can just return a number, which is shorthand for `{ offset: n }`.
 *
 * An intermediate result is yielded, when:
 *   - a term is found (`found === true`) and/or the search is stopped (`stop === true`);
 *   - a new generation is started;
 *   - the number of tries since the last yield exceeds `progressInterval`.
 * Such approach allows implementing progress bars
 * and prevents the search from blocking for too long.
 */
function * search<T> (seed: Expr[], options: SearchOptions, predicate: SearchCallback<T>): Generator<SearchProgress<T>> {
  const {
    depth = Infinity,
    infer = true,
    progressInterval = 1000,
  } = options;
  const hasSeen = infer && !options.noskip;

  const dummyProps: TermInfo = { steps: 0, normal: false, proper: false };

  // cache[i] = ith generation, 0 is seed generation
  const cache: Expr[][] = [[]];
  let gen = 0;
  let total = 0;
  let probed = 0;
  const seen: {[s: string]: boolean} = {};

  const store = (term: Expr, gen: number, offset: number = 0) => {
    if (offset < 0)
      return;
    if (!cache[gen + offset])
      cache[gen + offset] = [];
    cache[gen + offset].push(term);
  }

  const maybeProbe:(expr: Expr)=>{res: SearchCallbackResult<T>, props: TermInfo | undefined }
    = (term: Expr) => {
      total++;
      const props = infer ? term.infer({ max: options.max, maxArgs: options.maxArgs, maxSize: options.maxSize }) : dummyProps;
      if (hasSeen && props && props.expr) {
        // skip seen terms if allowed to
        const key = String(props.expr);
        if (seen[key])
          return { res: { offset: -1 }, props: undefined };
        seen[key] = true;
      }
      probed++;
      const res = predicate(term, props!);
      return (typeof res === 'object') ?  { res, props } : { res: { offset: res }, props };
    };

  // sieve through the seed
  for (const term of seed) {
    const { res } = maybeProbe(term);
    if (res.found !== undefined)
      yield { expr: term, found: res.found, gen: 0, total, probed, cache };
    if (res.stop) {
      // ensure at least one progress tick is always yielded
      yield { stop: true, gen: 0, total, probed, cache };
      return;
    }
    store(term, 0, res.offset ?? 0);
  }

  let lastProgress = 0;

  for (gen = 1; gen < depth; gen++) {
    yield { newGen: true, gen, total, probed, cache };
    if (!hasUpperHalf(gen, cache))
      break;

    lastProgress = total;

    for (let i = 0; i < gen; i++) {
      for (const a of cache[gen - i - 1] || []) {
        for (const b of cache[i] || []) {
          if (total >= (options.tries ?? Infinity)) {
            yield { stop: true, gen, total, probed, cache };
            return;
          }
          if (total - lastProgress >= progressInterval) {
            yield { gen, total, probed, cache };
            lastProgress = total;
          }
          const term = a.apply(b);
          const { res, props } = maybeProbe(term);

          if (res.found !== undefined)
            yield { expr: term, found: res.found, gen, total, probed, cache };
          if (res.stop) {
            yield { stop: true, gen, total, probed, cache };
            return;
          }
          if ((res.offset ?? 0) < 0)
            continue;

          // if the term is not reducible, it is more likely to be a dead end, so we push it further away
          const finalOffset = res.offset ?? (infer && props
            ? ((props.expr ? 0 : 3) + (props.duplicate ? 1 : 0) + (props.proper ? 0 : 1))
            : 0);
          store(term, gen, finalOffset);
        }
      }
    }
  }

  // last hopeless progress tick
  yield { stop: true, gen, total, probed, cache };
}

/**
 *  Returns a series of lambda terms equivalent to the given expression,
 *       up to the provided computation steps limit.
 *
 *       Unlike infer(), this will always return something,
 *       even if the expression has no normal form.
 *
 *       See also Expr.walk() and extras.toSKI().
 *
 * @param {Expr} expr
 * @param {{
 *   max?: number,
 *   maxArgs?: number,
 *   varGen?: function(void): FreeVar,
 *   steps?: number,
 *   html?: boolean,
 *   latin?: number,
 * }} options
 * @return {IterableIterator<{expr: Expr, steps?: number, comment?: string}>}
 */
function * toLambda (expr: Expr, options: { max?: number, maxArgs?: number } = {}) {
  let cur: Expr | null = naiveCanonize(expr, options);
  const seen = new Set(); // prune irreducible
  let steps = 0;
  while (cur) {
    const next = cur.traverse({ order: 'LI' }, e => {
      if (seen.has(e))
        return null;
      if (e instanceof App && e.fun instanceof Lambda) {
        const guess = e.infer({ max: options.max, maxArgs: options.maxArgs });
        steps += guess.steps ?? 0;
        if (!guess.normal) {
          seen.add(e);
          return null;
        }
        return control.stop(guess.expr);
      }
    });
    yield { expr: cur, steps };
    cur = next;
  }
}

/**
 *  Rewrite the expression into S, K, and I combinators step by step.
 *     Returns an iterator yielding the intermediate expressions,
 *     along with the number of steps taken to reach them.
 *
 *     See also Expr.walk() and extras.toLambda().
 *
 * @param {Expr} expr
 * @param {{max?: number}} [options]
 * @return {IterableIterator<{final: boolean, expr: Expr, steps: number}>}
 */
function * toSKI (expr: Expr, options: InferOptions & { identity?: boolean } = {}) {
  // options are ignored completely, TODO remove
  // get rid of non-lambdas
  let cur: Expr | null = naiveCanonize(expr, options);

  // TODO change defaults?
  const identity = options.identity ?? false ? builtin.I : builtin.S.apply(builtin.K, builtin.K);
  const kite = options.identity ?? false ? builtin.K.apply(builtin.I) : builtin.S.apply(builtin.K);

  let steps = 0;
  while (cur) {
    const next = cur.traverse({ order: 'LI' }, e => {
      if (!(e instanceof Lambda) || e.impl instanceof Lambda)
        return null; // continue

      if (e.impl === e.arg)
        return control.stop(identity);

      // special case for SK (don't want K(SKK) in output)
      if (e.impl === identity && !options.identity)
        return control.stop(kite);

      if (!e.impl.any(t => t === e.arg))
        return control.stop(builtin.K.apply(e.impl));
      // TODO use real assert here. e.impl contains e.arg and also isn't e.arg, in MUST be App.
      if (!(e.impl instanceof App))
        throw new Error('toSKI: assert failed: lambda body is of unexpected type ' + e.impl.constructor.name );
      // eta-reduction: body === (not e.arg) (e.arg)
      if (e.impl.arg === e.arg && !e.impl.fun.any(t => t === e.arg))
        return control.stop(e.impl.fun);
      // last resort, go S
      return control.stop(builtin.S.apply(new Lambda(e.arg, e.impl.fun), new Lambda(e.arg, e.impl.arg)));
    })
    yield { expr: cur, steps, final: !next };
    steps++;
    cur = next;
  }
}

// --- Utility functions ---

function isStringPair (x: unknown): string | undefined {
  return Array.isArray(x) && x.length === 2 && typeof x[0] === 'string' && typeof x[1] === 'string'
    ? undefined
    : 'must be a pair of strings';
}
function isStringTriple  (x: unknown): string | undefined {
  return Array.isArray(x) && x.length === 3 && typeof x[0] === 'string' && typeof x[1] === 'string' && typeof x[2] === 'string'
    ? undefined
    : 'must be a triplet of strings';
}

/**
 * Check if the upper half of a 2D array has any non-empty arrays.
 *
 * Used in search() to determine if any more generations are possible,
 * Since search() combines terms from the upper half with those in the lower half,
 * hasUpperHalf: false guarantees ALL future generations will be empty.
 *
 * @param gen - the current generation number
 * @param list - the 2D array of generations
 * @returns true if any non-empty arrays exist in the upper half of the list
 */
function hasUpperHalf<T> (gen: number, list: T[][]): boolean {
  if (gen < list.length)
    return true;
  for (let i = Math.floor(gen / 2); i < list.length; i++) {
    if (list[i] && list[i].length > 0)
      return true;
  }
  return false;
}

function naiveCanonize (expr: Expr, options: InferOptions): Expr {
  return expr.traverse(e => {
    if (e instanceof App || e instanceof Lambda || e instanceof FreeVar)
      return null;
    if (e instanceof Alias)
      return control.redo(e.impl);
    if (e instanceof Atomic && e.selfRef) {
      const dbl = new FreeVar('a');
      const M = new Lambda(dbl, dbl.apply(dbl));
      const half = e.source.subst(e.selfRef, e.selfRef.apply(e.selfRef)) ?? e.source;
      return M.apply(new Lambda(e.selfRef, naiveCanonize(half, options)) );
    }
    const guess = e.infer(options);
    if (!guess.expr)
      throw new Error('failed to canonize term ' + e);
    return guess.expr;
  }) ?? expr;
}

// --- Namespace export ---

export const extras = { search, deepFormat, toposort, checkFormatOptions, equiv, toLambda, toSKI };
